import React, { createContext, useContext, useReducer, ReactNode, useEffect, useState, useCallback } from 'react';
import { Notification } from '../types/notification';
import { v4 as uuidv4 } from 'uuid';
import { notificationsService } from '../services/supabase/notifications.service';
import logger from '../utils/logger';
import { eventsService } from '../services/events.service';
import { useUserFilter } from './UserFilterContext';

interface NotificationState {
  notifications: Notification[];
}

type NotificationAction =
  | { type: 'ADD_NOTIFICATION'; payload: Omit<Notification, 'id' | 'createdAt' | 'status'> }
  | { type: 'MARK_AS_READ'; payload: string }
  | { type: 'DELETE_NOTIFICATION'; payload: string }
  | { type: 'CLEAR_ALL' }
  | { type: 'SET_NOTIFICATIONS'; payload: Notification[] }
  | { type: 'UPDATE_NOTIFICATION'; payload: { id: string; updates: Partial<Notification> } };

const initialState: NotificationState = {
  notifications: []
};

function notificationReducer(state: NotificationState, action: NotificationAction): NotificationState {
  switch (action.type) {
    case 'ADD_NOTIFICATION':
      const newNotification: Notification = {
        id: uuidv4(),
        createdAt: new Date(),
        status: 'unread',
        ...action.payload
      };
      return {
        ...state,
        notifications: [newNotification, ...state.notifications]
      };
    case 'MARK_AS_READ':
      return {
        ...state,
        notifications: state.notifications.map(notification =>
          notification.id === action.payload
            ? { ...notification, status: 'read' }
            : notification
        )
      };
    case 'DELETE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter(notification => notification.id !== action.payload)
      };
    case 'CLEAR_ALL':
      return {
        ...state,
        notifications: []
      };
    case 'SET_NOTIFICATIONS':
      return {
        ...state,
        notifications: action.payload
      };
    case 'UPDATE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.map(notification =>
          notification.id === action.payload.id
            ? { ...notification, ...action.payload.updates }
            : notification
        )
      };
    default:
      return state;
  }
}

interface NotificationContextType {
  state: NotificationState;
  addNotification: (notification: Omit<Notification, 'id' | 'createdAt' | 'status'>) => void;
  markAsRead: (id: string) => void;
  deleteNotification: (id: string) => void;
  clearAll: () => void;
  setNotifications: (notifications: Notification[]) => void;
  updateNotification: (id: string, updates: Partial<Notification>) => void;
  fetchNotifications: () => Promise<void>; // Exposer la fonction pour permettre le rafraîchissement
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const [_loading, setLoading] = useState(false);
  const [_error, setError] = useState<Error | null>(null);

  // Traiter la file d'attente périodiquement
  const processQueue = useCallback(async () => {
    try {
      await notificationsService.processQueue();
    } catch (error) {
      logger.error('Erreur lors du traitement de la file d\'attente:', error);
    }
  }, []);

  // Traiter la file d'attente au démarrage et périodiquement
  useEffect(() => {
    // Traiter la file d'attente immédiatement au démarrage
    processQueue();

    // Configurer un intervalle pour traiter la file d'attente périodiquement
    const intervalId = setInterval(processQueue, 60000); // Toutes les minutes

    // Nettoyer l'intervalle lors du démontage
    return () => clearInterval(intervalId);
  }, [processQueue]);

  // Utiliser le contexte de filtrage d'utilisateur
  const { selectedUserId } = useUserFilter();

  // Fonction pour récupérer les notifications
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔔 NotificationContext - Chargement des notifications pour userId:', selectedUserId);
      // Passer le selectedUserId au service de notifications pour le filtrage
      const notifications = await notificationsService.getAll(selectedUserId);
      
      console.log('🔔 NotificationContext - Notifications chargées:', notifications.length);
      console.log('🔔 NotificationContext - Échantillon:', notifications.slice(0, 3).map(n => ({ 
        id: n.id, 
        title: n.title, 
        status: n.status, 
        type: n.type,
        markerId: n.markerId 
      })));
      
      dispatch({ type: 'SET_NOTIFICATIONS', payload: notifications });
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des notifications:', error);
      setLoading(false);
      setError(error as Error);
    }
  }, [selectedUserId]);

  // Charger les notifications au montage du composant et lorsque selectedUserId change
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications, selectedUserId]);
  
  // Écouter les événements de création de notification
  useEffect(() => {
    // S'abonner à l'événement NOTIFICATION_CREATED
    const unsubscribe = eventsService.subscribe('NOTIFICATION_CREATED', () => {
      logger.info('Rafraîchissement des notifications suite à une création');
      fetchNotifications();
    }, 'notification-context-refresh');
    
    return () => {
      unsubscribe();
    };
  }, [fetchNotifications]);
  
  // Écouter les événements de mise à jour des catégories de notifications
  useEffect(() => {
    // S'abonner à l'événement NOTIFICATIONS_CATEGORY_UPDATED
    const unsubscribe = eventsService.subscribe('NOTIFICATIONS_CATEGORY_UPDATED', (data: any) => {
      logger.info('Mise à jour des notifications suite à un changement de catégorie', data);
      
      // Mettre à jour l'état local sans recharger toutes les notifications
      if (data.notificationIds && data.notificationIds.length > 0 && data.newCategoryName) {
        // Mettre à jour les notifications affectées dans l'état local
        const updatedNotifications = state.notifications.map(notification => {
          if (data.notificationIds.includes(notification.id)) {
            return {
              ...notification,
              markerCategory: data.newCategoryName
            };
          }
          return notification;
        });
        
        // Mettre à jour l'état local
        dispatch({ type: 'SET_NOTIFICATIONS', payload: updatedNotifications });
        logger.info(`${data.notificationIds.length} notifications mises à jour en temps réel dans l'interface`);
      }
    }, 'notification-context');
    
    return () => {
      unsubscribe();
    };
  }, [state.notifications]);

  // Fonction pour ajouter une notification
  const addNotification = async (notification: Omit<Notification, 'id' | 'createdAt' | 'status'>) => {
    try {
      // Vérifier si une notification similaire existe déjà (pour éviter les doublons)
      const isDuplicate = state.notifications.some(existingNotif => 
        existingNotif.title === notification.title && 
        existingNotif.message === notification.message && 
        existingNotif.type === notification.type &&
        // Vérifier si la notification existante a été créée récemment (moins de 5 secondes)
        existingNotif.createdAt instanceof Date && 
        (new Date().getTime() - existingNotif.createdAt.getTime() < 5000)
      );

      if (isDuplicate) {
        logger.info('Notification similaire déjà existante, ignorant la nouvelle notification');
        return;
      }

      // Créer la notification dans Supabase
      const createdNotification = await notificationsService.create({
        ...notification,
        status: 'unread',
        createdAt: new Date()
      });
      
      // Mettre à jour l'état local
      dispatch({ type: 'SET_NOTIFICATIONS', payload: [createdNotification, ...state.notifications] });
      
      logger.info('Notification ajoutée:', createdNotification);
    } catch (error) {
      logger.error('Erreur lors de l\'ajout de la notification:', error);
      
      // Vérifier si une notification similaire existe déjà avant d'ajouter localement
      const isDuplicate = state.notifications.some(existingNotif => 
        existingNotif.title === notification.title && 
        existingNotif.message === notification.message && 
        existingNotif.type === notification.type
      );

      if (isDuplicate) {
        logger.info('Notification similaire déjà existante, ignorant la nouvelle notification');
        return;
      }
      
      // Même en cas d'erreur, ajouter la notification localement si ce n'est pas un doublon
      dispatch({
        type: 'ADD_NOTIFICATION',
        payload: notification
      });
    }
  };

  // Fonction pour marquer une notification comme lue
  const markAsRead = async (id: string) => {
    try {
      logger.info(`Marquage de la notification ${id} comme lue...`);
      
      // Mettre à jour l'état local immédiatement
      dispatch({ type: 'MARK_AS_READ', payload: id });
      
      // Appeler le service pour mettre à jour dans Supabase
      // Cette fonction gère maintenant la file d'attente et la persistance locale
      await notificationsService.markAsRead(id);
      
      logger.info(`Notification ${id} marquée comme lue avec succès`);
    } catch (error) {
      logger.error(`Erreur lors du marquage de la notification ${id} comme lue:`, error);
      // L'état local est déjà mis à jour, donc pas besoin de faire quoi que ce soit d'autre
    }
  };

  // Fonction pour supprimer une notification
  const deleteNotification = async (id: string) => {
    try {
      // Mettre à jour l'état local immédiatement
      dispatch({ type: 'DELETE_NOTIFICATION', payload: id });
      
      // Supprimer la notification dans Supabase
      // Cette fonction gère maintenant la file d'attente
      await notificationsService.delete(id);
      
      logger.info(`Notification ${id} supprimée avec succès`);
    } catch (error) {
      logger.error(`Erreur lors de la suppression de la notification ${id}:`, error);
      // L'état local est déjà mis à jour, donc pas besoin de faire quoi que ce soit d'autre
    }
  };

  // Fonction pour effacer toutes les notifications
  const clearAll = () => {
    dispatch({ type: 'CLEAR_ALL' });
  };

  // Fonction pour définir les notifications
  const setNotifications = (notifications: Notification[]) => {
    dispatch({ type: 'SET_NOTIFICATIONS', payload: notifications });
  };

  // Fonction pour mettre à jour une notification
  const updateNotification = async (id: string, updates: Partial<Notification>) => {
    try {
      // Mettre à jour l'état local immédiatement
      dispatch({ type: 'UPDATE_NOTIFICATION', payload: { id, updates } });
      
      // Mettre à jour la notification dans Supabase
      await notificationsService.update(id, updates);
      
      logger.info(`Notification ${id} mise à jour avec succès`);
    } catch (error) {
      logger.error(`Erreur lors de la mise à jour de la notification ${id}:`, error);
      // L'état local est déjà mis à jour, donc pas besoin de faire quoi que ce soit d'autre
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        state,
        addNotification,
        markAsRead,
        deleteNotification,
        clearAll,
        setNotifications,
        updateNotification,
        fetchNotifications
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

